import React from 'react';
import { useSearch } from '../contexts/SearchContext';
import { calculateDataRanges } from '../utils/calculateDataRanges';
import { calculatePriceSteps } from '../utils/calculatePriceSteps';
import { calculateMinimumGap } from '../utils/calculateMinimumGap';
import { calculateSliderStep } from '../utils/calculateSliderStep';
import SalesStatusToggle from './SalesStatusToggle';
import './SearchFilters.css';

export default function SearchFilters({ onResetSelection }) {
  const { 
    filters, 
    isExpanded, 
    handleSwitchChange, 
    handleDualRangeChange, 
    handleFeatureChange, 
    handlePropertyTypeChange, 
    toggleExpanded, 
    resetFilters, 
    properties,
    availablePropertyTypes,
    availableFeatures,
    hideFilterControls
  } = useSearch();
  
  // Calculate data ranges for sliders
  const dataRanges = calculateDataRanges(properties || []);
  
  // Calculate optimal price steps
  const priceSteps = calculatePriceSteps(dataRanges.price.min, dataRanges.price.max);

  // Calculate area step that ensures max thumb is reachable on native range grid
  const areaStep = calculateSliderStep(dataRanges.area.min, dataRanges.area.max, 0.1);
  
  // Check if there are any non-apartment properties
  const hasNonApartments = (properties || []).some(p => p.propertyType && p.propertyType !== 'lokal-mieszkalny');

  return (
    <div className={`filter-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="filters-container">
        {/* Status sprzedaży - always visible */}
        <SalesStatusToggle 
          isActive={filters.onlyAvailable}
          onToggle={() => handleSwitchChange('onlyAvailable')}
        />

        {/* Cena i Powierzchnia - visible when collapsed, only if there's variation */}
        {!isExpanded && (dataRanges.price.max > dataRanges.price.min || dataRanges.area.max > dataRanges.area.min) && (
          <div className="filter-section dual-range-section">
            {dataRanges.price.max > dataRanges.price.min && (
              <DualRangeSliderWithLabels
                label="Cena"
                min={dataRanges.price.min}
                max={dataRanges.price.max}
                step={priceSteps.step}
                values={filters.price}
                onChange={(values) => handleDualRangeChange('price', values)}
                tickCount={priceSteps.tickCount}
                snapPoints={priceSteps.snapPoints}
                formatValue={(value) => value.toLocaleString('pl-PL')}
                unit="zł"
              />
            )}
            {dataRanges.area.max > dataRanges.area.min && (
              <DualRangeSliderWithLabels
                label="Powierzchnia"
                min={dataRanges.area.min}
                max={dataRanges.area.max}
                values={filters.area}
                onChange={(values) => handleDualRangeChange('area', values)}
                tickCount={14}
                step={areaStep}
                noRounding={true}
                unit="m²"
              />
            )}
          </div>
        )}

        {/* Expanded filters */}
        {isExpanded && (
          <div className="expanded-filters">
            {/* Piętro - only in expanded and only if more than one floor */}
            {dataRanges.floor.max > dataRanges.floor.min && (
              <div className="filter-section number-buttons-section">
                <div className="filter-label-inline">
                  <span className="label-secondary">Piętro:</span>
                </div>
                <NumberButtons 
                  options={Array.from({length: dataRanges.floor.max - dataRanges.floor.min + 1}, (_, i) => dataRanges.floor.min + i)}
                  selectedValues={filters.floor}
                  onChange={(values) => handleDualRangeChange('floor', values)}
                  allowMultiple={true}
                />
              </div>
            )}

            {/* Liczba pokoi - full version in expanded, only if more than one room option */}
            {dataRanges.rooms.max > dataRanges.rooms.min && (
              <div className="filter-section number-buttons-section">
                <div className="filter-label-inline">
                  <span className="label-secondary">Ilość pokoi:</span>
                </div>
                <NumberButtons 
                  options={Array.from({length: dataRanges.rooms.max - dataRanges.rooms.min + 1}, (_, i) => dataRanges.rooms.min + i)}
                  selectedValues={filters.rooms}
                  onChange={(values) => handleDualRangeChange('rooms', values)}
                  allowMultiple={true}
                />
              </div>
            )}

            {/* Cena i Powierzchnia - only show if there's variation */}
            {(dataRanges.price.max > dataRanges.price.min || dataRanges.area.max > dataRanges.area.min) && (
              <div className="filter-section dual-range-section">
                {dataRanges.price.max > dataRanges.price.min && (
                  <DualRangeSliderWithLabels
                    label="Cena"
                    min={dataRanges.price.min}
                    max={dataRanges.price.max}
                    step={priceSteps.step}
                    values={filters.price}
                    onChange={(values) => handleDualRangeChange('price', values)}
                    tickCount={priceSteps.tickCount}
                    snapPoints={priceSteps.snapPoints}
                    formatValue={(value) => value.toLocaleString('pl-PL')}
                    unit="zł"
                  />
                )}
                {dataRanges.area.max > dataRanges.area.min && (
                  <DualRangeSliderWithLabels
                    label="Powierzchnia"
                    min={dataRanges.area.min}
                    max={dataRanges.area.max}
                    values={filters.area}
                    onChange={(values) => handleDualRangeChange('area', values)}
                    tickCount={10}
                    step={areaStep}
                    noRounding={true}
                    unit="m²"
                  />
                )}
              </div>
            )}
            {/* Typ lokalu - dynamic property type filter */}
            {(availablePropertyTypes || []).length > 1 && (
              <CheckboxGroup
                title="Typ lokalu"
                options={Object.fromEntries(
                  (availablePropertyTypes || []).map(type => [type.key, type.label])
                )}
                selectedValues={filters.propertyTypes}
                onChange={handlePropertyTypeChange}
              />
            )}

            {/* Cechy dodatkowe - dynamic features filter */}
            <CheckboxGroup
              title="Cechy dodatkowe"
              options={Object.fromEntries(
                (availableFeatures || []).map(feature => [feature.key, feature.label])
              )}
              selectedValues={filters.features}
              onChange={handleFeatureChange}
            />
          </div>
        )}

        {/* Reset and Toggle buttons - conditionally rendered based on hideFilterControls */}
        {!hideFilterControls && (
          <div className="bottom-buttons-container">
            <button className="reset-button" onClick={() => {
              resetFilters();
              if (onResetSelection) {
                onResetSelection();
              }
            }}>
              <svg 
                className="trash-icon"
                width="15" 
                height="15" 
                viewBox="0 0 24 24" 
                fill="none"
              >
                <path 
                  d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              <span>Resetuj filtry</span>
            </button>
            <button 
              className="toggle-button"
              onClick={toggleExpanded}
            >
              <span>{isExpanded ? 'Pokaż mniej opcji' : 'Pokaż więcej opcji'}</span>
              <svg 
                className={`caret-icon ${isExpanded ? 'expanded' : ''}`}
                width="15" 
                height="15" 
                viewBox="0 0 24 24" 
                fill="none"
              >
                <path 
                  d="M7 10L12 15L17 10" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// FilterSection component
function FilterSection({ label, value, children, isVisible, isCollapsed, isFeatures }) {
  if (!isVisible) return null;

  if (isCollapsed) {
    return (
      <div className="filter-section collapsed">
        <div className="filter-label">
          <span className="label-secondary">{label}:</span>
          {value && <span className="label-primary">{value}</span>}
        </div>
        <div className="filter-control-collapsed">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`filter-section ${isFeatures ? 'features' : ''}`}>
      <div className="filter-label">
        <span className="label-secondary">{label}:</span>
        {value && <span className="label-primary">{value}</span>}
      </div>
      <div className="filter-control">
        {children}
      </div>
    </div>
  );
}

// CheckboxGroup component
function CheckboxGroup({ title, options, selectedValues, onChange }) {
  return (
    <div className="filter-section features">
      <div className="features-title">{title}</div>
      <div className="features-grid">
        {Object.entries(options).map(([key, label]) => (
          <label key={key} className="checkbox-group" onClick={() => onChange(key)}>
            <div className="checkbox-wrapper">
              <div className="checkbox-outer" />
              <div className={`checkbox-inner ${selectedValues[key] ? 'checked' : ''}`} />
            </div>
            <span className="checkbox-label">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// DualRangeSliderWithLabels component
function DualRangeSliderWithLabels({ label, min, max, values, onChange, step = 1, tickCount, snapPoints, noRounding = false, formatValue, unit }) {
  const [isDragging, setIsDragging] = React.useState(null); // 'min', 'max', or null
  const containerRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const lastUpdateRef = React.useRef(0);

  // Calculate minimum gap to prevent handles from meeting
  const minimumGap = React.useMemo(() => {
    return calculateMinimumGap(step);
  }, [step]);

  const formatDisplayValue = (value) => {
    if (formatValue) {
      return formatValue(value);
    }
    return noRounding ? value.toFixed(1) : value;
  };

  // Convert position to value
  const positionToValue = React.useCallback((clientX) => {
    if (!containerRef.current) return null;
    
    const rect = containerRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return min + percent * (max - min);
  }, [min, max]);

  // Snap value to nearest snap point
  const snapValue = React.useCallback((value) => {
    if (!snapPoints || snapPoints.length === 0) return value;
    
    // If value is exactly min or max, return it
    if (value === min) return min;
    if (value === max) return max;
    
    return snapPoints.reduce((prev, curr) => 
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
  }, [snapPoints, min, max]);

  // Handle drag start
  const handlePointerDown = React.useCallback((e) => {
    // Only handle primary button (left click / touch)
    if (e.button !== 0) return;
    
    const value = positionToValue(e.clientX);
    if (value === null) return;

    // Determine which thumb is closer
    const distToMin = Math.abs(value - values[0]);
    const distToMax = Math.abs(value - values[1]);
    
    setIsDragging(distToMin <= distToMax ? 'min' : 'max');
    e.preventDefault();
  }, [positionToValue, values]);

  // Handle drag move with RAF throttling
  const handlePointerMove = React.useCallback((e) => {
    if (!isDragging) return;
    
    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    // Throttle updates using RAF
    rafRef.current = requestAnimationFrame(() => {
      const rawValue = positionToValue(e.clientX);
      if (rawValue === null) return;
      
      const snappedValue = snapValue(rawValue);
      
      // Only update if value actually changed
      const now = Date.now();
      if (now - lastUpdateRef.current > 16) { // ~60fps throttle
        if (isDragging === 'min') {
          // Enforce minimum gap: prevent min from reaching max
          const maxAllowedMin = values[1] - minimumGap;
          const newMin = Math.min(snappedValue, maxAllowedMin);
          if (newMin !== values[0]) {
            onChange([newMin, values[1]]);
          }
        } else {
          // Enforce minimum gap: prevent max from reaching min
          const minAllowedMax = values[0] + minimumGap;
          const newMax = Math.max(snappedValue, minAllowedMax);
          if (newMax !== values[1]) {
            onChange([values[0], newMax]);
          }
        }
        lastUpdateRef.current = now;
      }
    });
    
    e.preventDefault();
  }, [isDragging, positionToValue, snapValue, values, onChange, minimumGap]);

  // Handle drag end
  const handlePointerUp = React.useCallback(() => {
    setIsDragging(null);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
  }, []);

  // Attach global listeners when dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerUp);
      
      return () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp);
      };
    }
  }, [isDragging, handlePointerMove, handlePointerUp]);

  return (
    <div 
      className="dual-range-item" 
      ref={containerRef}
      onPointerDown={handlePointerDown}
      style={{ touchAction: 'none', userSelect: 'none' }}
    >
      <div className="filter-label">
        <span className="label-secondary">{label}:</span>
      </div>
      <DualRangeSlider
        min={min}
        max={max}
        step={step}
        values={values}
        onChange={onChange}
        tickCount={tickCount}
        snapPoints={snapPoints}
        noRounding={noRounding}
        disabled={isDragging !== null}
      />
      <div className="range-legend">
        <span className="range-min">{formatDisplayValue(values[0])} ({unit})</span>
        <span className="range-max">{formatDisplayValue(values[1])} ({unit})</span>
      </div>
    </div>
  );
}

// DualRangeSlider component
function DualRangeSlider({ min, max, values, onChange, tickCount, snapPoints: providedSnapPoints, step = 1, noRounding = false, disabled = false }) {
  // Create snap points based on tickCount or use provided ones
  const createSnapPoints = () => {
    if (providedSnapPoints && providedSnapPoints.length > 0) {
      // Upewnij się, że min i max są w liście
      const points = [...providedSnapPoints];
      if (!points.includes(min)) points.unshift(min);
      if (!points.includes(max)) points.push(max);
      return points.sort((a, b) => a - b);
    }
    
    const points = [];
    for (let i = 0; i <= tickCount - 1; i++) {
      const value = min + (i * (max - min)) / (tickCount - 1);
      points.push(noRounding ? value : Math.round(value));
    }
    return points;
  };

  const snapPoints = createSnapPoints();

  const minimumGap = React.useMemo(() => {
    return calculateMinimumGap(step);
  }, [step]);

  const snapToNearest = (value) => {
    // Jeśli wartość jest równa min lub max, zwróć ją bez zaokrąglania
    if (value === min) return min;
    if (value === max) return max;
    
    return snapPoints.reduce((prev, curr) => 
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
  };

  const handleMinChange = (e) => {
    const rawValue = parseFloat(e.target.value);
    const snappedValue = snapToNearest(rawValue);

    // Enforce minimum gap: prevent min from reaching max
    const maxAllowedMin = values[1] - minimumGap;
    const constrainedMin = Math.min(snappedValue, maxAllowedMin);

    const newValues = [constrainedMin, values[1]];
    onChange(newValues);
  };

  const handleMaxChange = (e) => {
    const rawValue = parseFloat(e.target.value);
    const snappedValue = snapToNearest(rawValue);

    // Enforce minimum gap: prevent max from reaching min
    const minAllowedMax = values[0] + minimumGap;
    const constrainedMax = Math.max(snappedValue, minAllowedMax);

    const newValues = [values[0], constrainedMax];
    onChange(newValues);
  };

  const handleTrackClick = (e) => {
    // Safari fix: ensure we have valid coordinates
    if (!e.clientX || !e.currentTarget) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return; // Safari sometimes returns 0 width
    
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const rawValue = noRounding ? (min + percent * (max - min)) : Math.round(min + percent * (max - min));
    const snappedValue = snapToNearest(rawValue);
    
    // Determine which thumb is closer
    const distToMin = Math.abs(snappedValue - values[0]);
    const distToMax = Math.abs(snappedValue - values[1]);
    
    if (distToMin <= distToMax) {
      const maxAllowedMin = values[1] - minimumGap;
      const constrainedMin = Math.min(snappedValue, maxAllowedMin);
      handleMinChange({ target: { value: constrainedMin.toString() } });
    } else {
      const minAllowedMax = values[0] + minimumGap;
      const constrainedMax = Math.max(snappedValue, minAllowedMax);
      handleMaxChange({ target: { value: constrainedMax.toString() } });
    }
  };

  // Oblicz procenty bazując na rzeczywistych pozycjach step w input range
  const getPercent = (value) => {
    // Znajdź najbliższą pozycję step dla tej wartości
    const stepsFromMin = Math.round((value - min) / step);
    const totalPossibleSteps = Math.floor((max - min) / step);
    
    // Jeśli wartość to max, ale input nie może do niej dotrzeć, użyj max możliwej pozycji
    if (value === max && totalPossibleSteps * step < (max - min)) {
      return 100; // Pokazuj max jako 100% wizualnie
    }
    
    // Normalne obliczenie bazujące na pozycji step
    const percent = (stepsFromMin / totalPossibleSteps) * 100;
    return Math.round(Math.min(100, Math.max(0, percent)));
  };
  
  const minPercent = getPercent(values[0]);
  const maxPercent = getPercent(values[1]);

  // Touch event handlers for better mobile responsiveness
  const handleTouchStart = (e) => {
    e.target.focus();
  };

  return (
    <div className="dual-range-slider" onClick={disabled ? undefined : handleTrackClick}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={values[0]}
        onChange={handleMinChange}
        onTouchStart={handleTouchStart}
        className="range-slider-min"
        style={{ 
          zIndex: values[0] > max - (max - min) / 2 ? 5 : 3,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
        disabled={disabled}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={values[1]}
        onChange={handleMaxChange}
        onTouchStart={handleTouchStart}
        className="range-slider-max"
        style={{ 
          zIndex: values[0] > max - (max - min) / 2 ? 3 : 5,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
        disabled={disabled}
      />
      <div className="slider-track" />
      <div 
        className="slider-range" 
        style={{
          left: `${minPercent}%`,
          width: `${maxPercent - minPercent}%`
        }}
      />
    </div>
  );
}

// NumberButtons component for floor and room selection
function NumberButtons({ options, selectedValues, onChange, allowMultiple = true }) {
  // Convert range format to array of selected values
  const getSelectedArray = () => {
    if (!selectedValues || selectedValues.length !== 2) return options;
    const [min, max] = selectedValues;
    
    // If min equals max and they are at the extremes, select all
    if (min === max && min === Math.min(...options) && max === Math.max(...options)) {
      return options;
    }
    
    const selected = [];
    for (let i = min; i <= max; i++) {
      if (options.includes(i)) {
        selected.push(i);
      }
    }
    return selected;
  };

  const selectedArray = getSelectedArray();
  
  const handleButtonClick = (value) => {
    const currentSelected = selectedArray;
    
    if (currentSelected.includes(value)) {
      // If clicking on selected value and it's the only one, select all
      if (currentSelected.length === 1) {
        onChange([Math.min(...options), Math.max(...options)]);
      } else {
        // Remove this value from selection
        const newSelected = currentSelected.filter(v => v !== value);
        const min = Math.min(...newSelected);
        const max = Math.max(...newSelected);
        onChange([min, max]);
      }
    } else {
      // Add value to selection and create continuous range
      const newSelected = [...currentSelected, value].sort((a, b) => a - b);
      const min = Math.min(...newSelected);
      const max = Math.max(...newSelected);
      onChange([min, max]);
    }
  };

  const isSelected = (value) => {
    return selectedArray.includes(value);
  };

  return (
    <div className="number-buttons">
      {options.map((option) => (
        <button
          key={option}
          className={`number-button ${isSelected(option) ? 'selected' : ''}`}
          onClick={() => handleButtonClick(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}